// src/pages/TraineeTasksDashboardPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, Timestamp, where, deleteField, getDoc // ✅ added getDocs
} from 'firebase/firestore';
import { intervalToDuration } from 'date-fns';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/styleUtils';
import styles from '../styles/TraineeTasksDashboardPage.module.css';

// Task Statuses
const TASK_STATUSES_INTERNAL = ['To Do', 'In Progress', 'Submitted', 'Needs Revision', 'Completed', 'Blocked'];
const DISPLAY_COLUMNS_ORDER = ['To Do', 'In Progress', 'Submitted', 'Needs Revision', 'Completed', 'Blocked'];

const TASK_PRIORITIES = ['High', 'Medium', 'Low'];
const TASK_ACTION_ROLES = ['Manager', 'Accounts Manager'];
const SUPERVISOR_ROLE_NAME = 'Supervisor';

/* =========================
   Helpers for "fired/inactive"
   ========================= */
const toLower = (v) => (typeof v === 'string' ? v.toLowerCase() : '');
const toDateObj = (v) => {
  if (!v) return null;
  if (v?.toDate) { try { return v.toDate(); } catch { /* ignore */ } }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
const isFiredOrInactive = (rec) => {
  if (!rec) return false;
  const inactiveFlag = rec?.active === false || rec?.isActive === false;
  const firedFlag = rec?.isFired === true;
  const statusStr = toLower(rec?.employmentStatus || rec?.status || '');
  const statusFlag = ['fired', 'terminated', 'inactive', 'disabled'].includes(statusStr);
  const endDate = toDateObj(rec?.employmentEndDate);
  const endDateFlag = endDate ? endDate.getTime() <= Date.now() : false;
  return inactiveFlag || firedFlag || statusFlag || endDateFlag;
};
const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};

/* =========================
   Existing helpers (dates/status UI)
   ========================= */
const parseDateSafely = (dateValue) => {
  if (!dateValue) return null;
  try {
    if (dateValue instanceof Timestamp) { return dateValue.toDate(); }
    if (typeof dateValue === 'string') {
      const dateString = dateValue.length === 10 && !dateValue.includes('T') ? dateValue + 'T00:00:00Z' : dateValue;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        const parts = dateValue.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const day = parseInt(parts[2], 10);
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            const utcDate = new Date(Date.UTC(year, month, day));
            if (!isNaN(utcDate.getTime())) return utcDate;
          }
        }
        console.warn("Invalid date string for parseDateSafely:", dateValue); return null;
      }
      return date;
    }
    if (dateValue instanceof Date && !isNaN(dateValue)) { return dateValue; }
    console.warn("Unknown or invalid date format for parseDateSafely:", dateValue); return null;
  } catch (error) { console.error("Error parsing date in parseDateSafely:", error, dateValue); return null; }
};

const isDateInRange = (dateFieldValue, rangeStart, rangeEnd) => {
  if (rangeStart === null && rangeEnd === null) return !!dateFieldValue;
  if (!dateFieldValue) return false;
  const dateToTest = parseDateSafely(dateFieldValue);
  if (!dateToTest) return false;
  let inRange = true;
  if (rangeStart && dateToTest < rangeStart) inRange = false;
  if (rangeEnd && dateToTest > rangeEnd) inRange = false;
  return inRange;
};

const formatAverageDuration = (averageMilliseconds) => {
  if (averageMilliseconds === null || averageMilliseconds < 0 || isNaN(averageMilliseconds)) return 'N/A';
  if (averageMilliseconds === 0) return 'Instantly';
  const endMs = Math.max(0, averageMilliseconds);
  const duration = intervalToDuration({ start: 0, end: endMs });
  const parts = [];
  if (duration.days && duration.days > 0) parts.push(`${duration.days}d`);
  if (duration.hours && duration.hours > 0) parts.push(`${duration.hours}h`);
  if (parts.length < 2 && duration.minutes && duration.minutes > 0) parts.push(`${duration.minutes}m`);
  if (parts.length === 0) {
    if (duration.seconds && duration.seconds > 0) {
      parts.push(`${duration.seconds}s`);
    } else if (averageMilliseconds > 0) {
      return '< 1s';
    } else { return '0s'; }
  }
  if (parts.length === 0 && averageMilliseconds > 0) return '< 1m';
  if (parts.length === 0) return 'N/A';
  return parts.slice(0, 2).join(' ');
};

const isTaskOverdueForTrainee = (task) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const dueDate = parseDateSafely(task.dueDate);
  const statusLower = task.status?.toLowerCase();
  if (!dueDate || statusLower === 'completed' || statusLower === 'cancelled') {
    return false;
  }
  return dueDate < today;
};

const getStatusClass = (status, type = 'badge') => {
  const statusKey = status?.toLowerCase().replace(/\s+/g, '') || 'default';
  if (type === 'badge') {
    switch (statusKey) {
      case 'todo': return styles.statusBadgeToDo;
      case 'inprogress': return styles.statusBadgeInProgress;
      case 'submitted': return styles.statusBadgeSubmitted;
      case 'needsrevision': return styles.statusBadgeNeedsRevision;
      case 'completed': return styles.statusBadgeCompleted;
      case 'blocked': return styles.statusBadgeBlocked;
      default: return styles.statusBadgeDefault;
    }
  } else if (type === 'border') {
    switch (statusKey) {
      case 'todo': return styles.statusBorderToDo;
      case 'inprogress': return styles.statusBorderInProgress;
      case 'submitted': return styles.statusBorderSubmitted;
      case 'needsrevision': return styles.statusBorderNeedsRevision;
      case 'completed': return styles.statusBorderCompleted;
      case 'blocked': return styles.statusBorderBlocked;
      default: return styles.statusBorderDefault;
    }
  }
  return '';
};

const TraineeTasksDashboardPage = () => {
  const { currentUser, userRole, isSupervisor } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [assignableTrainees, setAssignableTrainees] = useState([]);
  const [supervisedTraineeUids, setSupervisedTraineeUids] = useState([]);
  const [supervisedTraineeUidsFetched, setSupervisedTraineeUidsFetched] = useState(false);

  // 🔎 live user-status map for fired/inactive detection
  const [userStatusByUid, setUserStatusByUid] = useState(new Map());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [taskForSubmission, setTaskForSubmission] = useState(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionLinks, setSubmissionLinks] = useState(['']);
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [fileUploadProgress, setFileUploadProgress] = useState({});

  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [taskForRevisionRequest, setTaskForRevisionRequest] = useState(null);
  const [revisionReasonText, setRevisionReasonText] = useState('');

  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [completionFilter, setCompletionFilter] = useState('all');

  const [submitError, setSubmitError] = useState('');

  const [achievementsDateFilter, setAchievementsDateFilter] = useState('today');
  const [customAchievementStartDate, setCustomAchievementStartDate] = useState('');
  const [customAchievementEndDate, setCustomAchievementEndDate] = useState('');

  const isMounted = useRef(true);
  const messageTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const isProcessingAction = isProcessing || isSubmitting || isUploadingFile;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (successMessage || error || submitError) {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          setSuccessMessage('');
          if (!showTaskModal && !showSubmissionModal && !showRevisionModal) {
            setError('');
            setSubmitError('');
          }
        }
      }, 4000);
    }
    return () => { if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current); };
  }, [successMessage, error, submitError, showTaskModal, showSubmissionModal, showRevisionModal]);

  /* =========================
     Live users map for fired/inactive
     ========================= */
  useEffect(() => {
    const usersRef = collection(db, 'users');
    const unsub = onSnapshot(usersRef, (snapshot) => {
      const m = new Map();
      snapshot.docs.forEach(d => m.set(d.id, d.data()));
      if (isMounted.current) setUserStatusByUid(m);
    }, (err) => {
      console.error('users onSnapshot error:', err);
    });
    return () => unsub();
  }, []);

  /* =========================
     Load trainees (hide fired/inactive)
     ========================= */
  useEffect(() => {
    if (currentUser && (TASK_ACTION_ROLES.includes(userRole) || isSupervisor || userRole === 'Trainee')) {
      const traineesColRef = collection(db, 'trainees');
      const qT = query(traineesColRef, orderBy('name', 'asc'));
      const unsubscribe = onSnapshot(qT, (snapshot) => {
        if (!isMounted.current) return;
        // include possible status fields from trainee doc
        const traineesRaw = snapshot.docs.map(d => {
          const data = d.data();
          return {
            docId: d.id,
            authUid: data.authUid,
            name: data.name,
            // potential status fields:
            active: data.active,
            isActive: data.isActive,
            isFired: data.isFired,
            employmentStatus: data.employmentStatus,
            status: data.status,
            employmentEndDate: data.employmentEndDate,
          };
        }).filter(t => t.authUid && t.name);

        // Merge with /users status if present; hide fired/inactive
        const filtered = traineesRaw.filter(t => {
          const u = userStatusByUid.get(t.authUid);
          return !isFiredOrInactive({ ...u, ...t });
        });

        setAssignableTrainees(filtered);
      }, (err) => {
        console.error("Error fetching assignable trainees:", err);
        if (isMounted.current) setError(prev => (prev ? prev + " | " : "") + "Failed to load trainees list.");
      });
      return () => unsubscribe();
    } else {
      if (isMounted.current) setAssignableTrainees([]);
    }
  }, [currentUser, userRole, isSupervisor, userStatusByUid]);

  /* =========================
     Tasks feed
     ========================= */
  useEffect(() => {
    if (!currentUser) {
      if (isMounted.current) {
        setLoading(false); setTasks([]); setError("Please log in.");
      }
      return;
    }
    if (isMounted.current) {
      setLoading(true); setError('');
    }
    const tasksCollectionRef = collection(db, 'tasks');
    let q;

    const baseQueryConstraints = [where('taskSystem', '==', 'trainee')];

    if (TASK_ACTION_ROLES.includes(userRole)) {
      q = query(tasksCollectionRef,
        ...baseQueryConstraints,
        orderBy('traineeDocId'),
        orderBy('createdAt', 'desc')
      );
    } else if (isSupervisor) {
      if (!supervisedTraineeUidsFetched) { return; }
      if (supervisedTraineeUids.length > 0) {
        if (supervisedTraineeUids.length > 30) {
          if (isMounted.current) {
            setError("Error: Too many supervised trainees for efficient display.");
            setTasks([]); setLoading(false);
          }
          return;
        }
        q = query(tasksCollectionRef,
          ...baseQueryConstraints,
          where('assignedToUid', 'in', supervisedTraineeUids),
          orderBy('createdAt', 'desc')
        );
      } else {
        if (isMounted.current) { setTasks([]); setLoading(false); }
        return;
      }
    } else {
      q = query(tasksCollectionRef,
        ...baseQueryConstraints,
        where('assignedToUid', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!isMounted.current) return;
      const fetchedTasks = querySnapshot.docs.map(d => ({
        id: d.id, ...d.data(),
        dueDate: d.data().dueDate?.toDate(),
        createdAt: d.data().createdAt?.toDate(),
        updatedAt: d.data().updatedAt?.toDate(),
        acknowledgedAt: d.data().acknowledgedAt?.toDate(),
        submittedAt: d.data().submittedAt?.toDate(),
        revisionRequestedAt: d.data().revisionRequestedAt?.toDate(),
        completedAt: d.data().completedAt?.toDate()
      }));
      setTasks(fetchedTasks);
      setLoading(false);
    }, (err) => {
      if (!isMounted.current) return;
      console.error("Error fetching tasks:", err);
      if (err.code === 'failed-precondition' && err.message.includes('index')) {
        setError(`Database index required: ${err.message}. Please follow the link in the console to create it or contact an administrator.`);
      } else {
        setError("Failed to load tasks.");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser, userRole, isSupervisor, supervisedTraineeUids, supervisedTraineeUidsFetched]);

  const canPerformBroadTaskActions = useMemo(() => TASK_ACTION_ROLES.includes(userRole), [userRole]);
  const canCreateTasks = useMemo(() =>
    TASK_ACTION_ROLES.includes(userRole) || isSupervisor || userRole === 'Trainee',
    [userRole, isSupervisor]
  );
  const showAssigneeFilter = useMemo(() => TASK_ACTION_ROLES.includes(userRole) || isSupervisor, [userRole, isSupervisor]);

  const filteredTasksByAssignee = useMemo(() => {
    if (assigneeFilter === 'all' || !showAssigneeFilter) return tasks;
    return tasks.filter(task => task.assignedToUid === assigneeFilter);
  }, [tasks, assigneeFilter, showAssigneeFilter]);

  const tasksByDisplayColumn = useMemo(() => {
    const grouped = {};
    DISPLAY_COLUMNS_ORDER.forEach(colName => grouped[colName] = []);
    (filteredTasksByAssignee || []).forEach(task => {
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      } else {
        console.warn(`Task ${task.id} has an unmapped status: ${task.status}. Defaulting to 'Blocked'.`);
        grouped['Blocked'].push(task);
      }
    });
    for (const colName in grouped) {
      grouped[colName].sort((a, b) => {
        const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3, 'N/A': 4 };
        if ((priorityOrder[a.priority] || 4) !== (priorityOrder[b.priority] || 4)) {
          return (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
        }
        if (a.dueDate && b.dueDate) return a.dueDate - b.dueDate;
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return (a.createdAt && b.createdAt) ? b.createdAt - a.createdAt : 0;
      });
    }
    return grouped;
  }, [filteredTasksByAssignee]);

  const achievementDateRange = useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date();
    end.setHours(23, 59, 59, 999);

    switch (achievementsDateFilter) {
      case 'today': start.setHours(0, 0, 0, 0); break;
      case 'yesterday':
        start = new Date(today); start.setDate(today.getDate() - 1); start.setHours(0, 0, 0, 0);
        end = new Date(today); end.setDate(today.getDate() - 1); end.setHours(23, 59, 59, 999);
        break;
      case 'last7':
        start = new Date(today); start.setDate(today.getDate() - 6); start.setHours(0, 0, 0, 0);
        break;
      case 'last28':
        start = new Date(today); start.setDate(today.getDate() - 27); start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        if (customAchievementStartDate && customAchievementEndDate) {
          start = parseDateSafely(customAchievementStartDate + "T00:00:00");
          end = parseDateSafely(customAchievementEndDate + "T23:59:59.999");
          if (!start || !end || end < start) return { start: null, end: null, isValid: false, filterType: 'custom' };
        } else return { start: null, end: null, isValid: false, filterType: 'custom' };
        break;
      case 'maximum': default: return { start: null, end: null, isValid: true, filterType: 'maximum' };
    }
    return { start, end, isValid: true, filterType: achievementsDateFilter };
  }, [achievementsDateFilter, customAchievementStartDate, customAchievementEndDate]);

  const traineeStats = useMemo(() => {
    const defaults = {
      activeTasks: 0, submittedInRange: 0, completedInRange: 0,
      needsRevisionInRange: 0, overdueInRange: 0,
      avgAckTime: null, avgWorkCycleTime: null,
      ackTasksCount: 0, submittedTasksForCycleCount: 0
    };

    const targetUid = (isSupervisor && assigneeFilter !== 'all') ? assigneeFilter : currentUser?.uid;
    if (!currentUser || !targetUid || tasks.length === 0 || (achievementsDateFilter === 'custom' && !achievementDateRange.isValid)) {
      return defaults;
    }

    const { start: rangeStart, end: rangeEnd, filterType } = achievementDateRange;

    let activeTasks = 0;
    let submittedInRange = 0;
    let completedInRange = 0;
    let needsRevisionInRange = 0;
    let overdueInRange = 0;
    let totalAckTime = 0;
    let ackTasksCount = 0;
    let totalWorkCycleTime = 0;
    let submittedTasksForCycleCount = 0;

    const relevantTasks = tasks.filter(task => task.assignedToUid === targetUid);

    relevantTasks.forEach(task => {
      const createdAtDate = parseDateSafely(task.createdAt);
      const acknowledgedAtDate = parseDateSafely(task.acknowledgedAt);
      const submittedAtDate = parseDateSafely(task.submittedAt);
      const completedAtDate = parseDateSafely(task.completedAt);
      const revisionRequestedAtDate = parseDateSafely(task.revisionRequestedAt);
      const dueDate = parseDateSafely(task.dueDate);

      if (!['Completed', 'Cancelled'].includes(task.status)) {
        if (filterType === 'maximum' || isDateInRange(createdAtDate, rangeStart, rangeEnd) || isDateInRange(acknowledgedAtDate, rangeStart, rangeEnd)) {
          activeTasks++;
        }
      }
      if (task.status === 'Submitted' && isDateInRange(submittedAtDate, rangeStart, rangeEnd)) {
        submittedInRange++;
      }
      if (task.status === 'Completed' && isDateInRange(completedAtDate, rangeStart, rangeEnd)) {
        completedInRange++;
      }
      if (task.status === 'Needs Revision' && isDateInRange(revisionRequestedAtDate, rangeStart, rangeEnd)) {
        needsRevisionInRange++;
      }
      if (isTaskOverdueForTrainee(task)) {
        if (filterType === 'maximum' || isDateInRange(dueDate, rangeStart, rangeEnd) || (!task.completedAt && dueDate < (rangeEnd || new Date()))) {
          overdueInRange++;
        }
      }
      if (createdAtDate && acknowledgedAtDate && isDateInRange(acknowledgedAtDate, rangeStart, rangeEnd)) {
        const diff = acknowledgedAtDate.getTime() - createdAtDate.getTime();
        if (diff >= 0) {
          totalAckTime += diff;
          ackTasksCount++;
        }
      }
      if (acknowledgedAtDate && submittedAtDate && isDateInRange(submittedAtDate, rangeStart, rangeEnd)) {
        const diff = submittedAtDate.getTime() - acknowledgedAtDate.getTime();
        if (diff >= 0) {
          totalWorkCycleTime += diff;
          submittedTasksForCycleCount++;
        }
      }
    });

    return {
      activeTasks, submittedInRange, completedInRange, needsRevisionInRange, overdueInRange,
      avgAckTime: ackTasksCount > 0 ? totalAckTime / ackTasksCount : null,
      avgWorkCycleTime: submittedTasksForCycleCount > 0 ? totalWorkCycleTime / submittedTasksForCycleCount : null,
      ackTasksCount,
      submittedTasksForCycleCount
    };
  }, [tasks, currentUser, achievementDateRange, achievementsDateFilter, isSupervisor, assigneeFilter]);

  /* =========================
     Vacation conflict check
     ========================= */
      // NEW: single-day check by known doc id: attendance/{uid}_{YYYY-MM-DD}
      const checkVacationForDayById = async (uid, ymd) => {
        if (!uid || !ymd) return [];
        const attendanceId = `${uid}_${ymd}`;
        const ref = doc(db, 'attendance', attendanceId);
        const snap = await getDoc(ref);
        if (!snap.exists()) return [];
        const d = snap.data();
        return d?.status === 'Vacation'
          ? [{ date: ymd, type: d?.vacation?.type || '', note: d?.vacation?.note || '' }]
          : [];
      };

  /* =========================
     Actions
     ========================= */
  const handleAcknowledgeTask = async (taskId) => {
    if (!currentUser) return;
    const taskRef = doc(db, 'tasks', taskId);
    try {
      await updateDoc(taskRef, {
        status: 'In Progress',
        acknowledgedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (isMounted.current) setSuccessMessage("Task acknowledged and moved to In Progress.");
    } catch (err) {
      console.error("Error acknowledging task:", err);
      if (isMounted.current) setError("Failed to acknowledge task.");
    }
  };

  // Close the "Request Revision" modal
const handleCloseRevisionModal = () => {
    setShowRevisionModal(false);
    setTaskForRevisionRequest(null);
    setRevisionReasonText('');
    if (isMounted.current) setError('');
  };
  
  // Submit the revision request
  const handleSubmitRevisionRequest = async () => {
    if (!taskForRevisionRequest || !revisionReasonText.trim()) {
      if (isMounted.current) setError("Reason for revision is required.");
      return;
    }
    if (isMounted.current) { setIsProcessing(true); setError(''); }
  
    try {
      const taskRef = doc(db, 'tasks', taskForRevisionRequest.id);
      await updateDoc(taskRef, {
        status: 'Needs Revision',
        revisionReason: revisionReasonText.trim(),
        revisionRequestedAt: serverTimestamp(),
        submittedAt: null,
        submission: deleteField(),
        completedAt: null,
        updatedAt: serverTimestamp(),
        taskSystem: 'trainee',
      });
      if (isMounted.current) {
        setSuccessMessage("Revision requested successfully. Task moved to 'Needs Revision'.");
        handleCloseRevisionModal();
      }
    } catch (err) {
      console.error("Error submitting revision request:", err);
      if (isMounted.current) setError("Failed to request revision: " + err.message);
    } finally {
      if (isMounted.current) setIsProcessing(false);
    }
  };
  

  const handleOpenSubmissionModal = (task) => {
    setTaskForSubmission(task);
    setSubmissionText(task.submission?.text || (task.status === 'Needs Revision' && task.submission?.text) || '');
    setSubmissionLinks(task.submission?.links && task.submission.links.length > 0 ? task.submission.links : ['']);
    setSubmissionFiles([]);
    setFileUploadProgress({});
    setShowSubmissionModal(true);
    if(isMounted.current) {setError(''); setSubmitError('');}
  };
  const handleCloseSubmissionModal = () => {
    setShowSubmissionModal(false);
    setTaskForSubmission(null);
    setSubmissionText('');
    setSubmissionLinks(['']);
    setSubmissionFiles([]);
    setFileUploadProgress({});
    setSubmitError('');
  };
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (submissionFiles.length + files.length > 10) { if(isMounted.current) setSubmitError("Max 10 files."); return; }
    const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024);
    if (validFiles.length < files.length) { if(isMounted.current) setSubmitError(`Some files too large (max 5MB).`); }
    setSubmissionFiles(prev => [...prev, ...validFiles.slice(0, 10 - prev.length)]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeSubmissionFile = (fileName) => {
    setSubmissionFiles(prev => prev.filter(f => f.name !== fileName));
    setFileUploadProgress(prev => { const newProg = {...prev}; delete newProg[fileName]; return newProg; });
  };

  const addLinkField = () => { if (submissionLinks.length < 10) setSubmissionLinks([...submissionLinks, '']); };
  const updateLinkField = (index, value) => { const newLinks = [...submissionLinks]; newLinks[index] = value; setSubmissionLinks(newLinks); };
  const removeLinkField = (index) => { if (submissionLinks.length > 1) setSubmissionLinks(submissionLinks.filter((_, i) => i !== index)); else if (submissionLinks.length === 1) setSubmissionLinks(['']); };

  const handleSubmitWork = async () => {
    if (!taskForSubmission || !currentUser) return;
    if (isMounted.current) { setIsSubmitting(true); setSubmitError('');}

    const trimmedSubmissionText = submissionText.trim();
    const validLinks = submissionLinks.filter(link => link.trim().length > 0);
    const filesProvided = submissionFiles.length > 0;

    const textProvided = trimmedSubmissionText.length > 0;
    const linksProvided = validLinks.length > 0;

    const submissionOptionsCount = (textProvided ? 1 : 0) + (linksProvided ? 1 : 0) + (filesProvided ? 1 : 0);

    if (submissionOptionsCount === 0) {
      setSubmitError("Please provide a submission: either text notes, links, or files.");
      if(isMounted.current) setIsSubmitting(false);
      return;
    }

    if (submissionOptionsCount > 1) {
      setSubmitError("Please submit only one type of content (text, links, or files), not a combination.");
      if(isMounted.current) setIsSubmitting(false);
      return;
    }

    const uploadedFileMetadata = [];
    if (filesProvided) {
      console.warn("File upload to Firebase Storage is NOT IMPLEMENTED. Using placeholders.");
      for (const file of submissionFiles) {
        uploadedFileMetadata.push({
          name: file.name, type: file.type, size: file.size,
          url: `simulated_url_for_${file.name}`,
          storagePath: `taskSubmissions/${taskForSubmission.id}/${Date.now()}_${file.name}`
        });
      }
    }

    const taskRef = doc(db, 'tasks', taskForSubmission.id);
    try {
      const submissionDataObject = {};
      if (textProvided) submissionDataObject.text = trimmedSubmissionText;
      if (linksProvided) submissionDataObject.links = validLinks;
      if (filesProvided) submissionDataObject.files = uploadedFileMetadata;

      await updateDoc(taskRef, {
        status: 'Submitted', submittedAt: serverTimestamp(),
        submission: Object.keys(submissionDataObject).length > 0 ? submissionDataObject : deleteField(),
        revisionReason: null,
        revisionRequestedAt: null,
        completedAt: null,
        updatedAt: serverTimestamp(),
      });
      if (isMounted.current) { setSuccessMessage("Work submitted successfully."); handleCloseSubmissionModal(); }
    } catch (err) {
      console.error("Error submitting work:", err);
      if (isMounted.current) setSubmitError("Failed to submit work. " + err.message);
    } finally {
      if (isMounted.current) setIsSubmitting(false);
    }
  };

  const handleSupervisorDecision = async (taskId, decision) => {
    if (!currentUser || (!canPerformBroadTaskActions && !isSupervisor)) return;
    const taskToUpdate = tasks.find(t => t.id === taskId);
    if (!taskToUpdate) { if (isMounted.current) setError("Task not found."); return; }

    if (decision === 'Needs Revision') {
      setTaskForRevisionRequest(taskToUpdate);
      setRevisionReasonText('');
      setShowRevisionModal(true);
    } else if (decision === 'Completed') {
      if (isMounted.current) setIsProcessing(true);
      const taskRef = doc(db, 'tasks', taskId);
      try {
        await updateDoc(taskRef, {
          status: 'Completed',
          revisionReason: null,
          revisionRequestedAt: null,
          completedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        if (isMounted.current) setSuccessMessage(`Task "${taskToUpdate.title}" approved & marked as Completed.`);
      } catch (err) {
        console.error("Error approving task:", err);
        if (isMounted.current) setError("Failed to approve task.");
      } finally {
        if (isMounted.current) setIsProcessing(false);
      }
    }
  };

  const {
    handleOpenAddTaskModal,
    handleOpenEditTaskModal,
    handleCloseModal: handleCloseAddEditModal,
    handleSaveTask,
    handleDeleteTask
  } = (() => {
    const _hOATM = () => {
      if (!canCreateTasks) {
        if (isMounted.current) setError("Permission denied to add tasks.");
        return;
      }
      if ((userRole === 'Trainee' || isSupervisor || TASK_ACTION_ROLES.includes(userRole)) && assignableTrainees.length === 0 && !loading) {
        if(isMounted.current) setError("Trainee list is loading. Please try again shortly to assign tasks.");
        return;
      }
      if (isMounted.current) {
        setEditingTask(null);
        setShowTaskModal(true);
        setError('');
        setSuccessMessage('');
      }
    };

    const _hOETM = (task) => {
      if (!canPerformBroadTaskActions && !(isSupervisor && supervisedTraineeUids.includes(task.assignedToUid))) {
        if (isMounted.current) setError("Permission denied to edit this task.");
        return;
      }
      if ((userRole === 'Trainee' || isSupervisor || TASK_ACTION_ROLES.includes(userRole)) && assignableTrainees.length === 0 && !loading ) {
        if(isMounted.current) setError("Trainee list is loading. Please try again shortly.");
        return;
      }
      if (isMounted.current) {
        setEditingTask(task);
        setShowTaskModal(true);
        setError('');
        setSuccessMessage('');
      }
    };

    const _hCM = () => {
      if (isMounted.current) {
        setShowTaskModal(false);
        setEditingTask(null);
        setError('');
      }
    };

    const _hST = async (formData) => {
      if (!currentUser) {
        if (isMounted.current) setError("Authentication error.");
        return;
      }
      const isCreating = !editingTask;

      if (isCreating && !canCreateTasks) {
        if (isMounted.current) setError("Permission denied to create tasks.");
        return;
      }
      if (!isCreating && !canPerformBroadTaskActions &&
          !(isSupervisor && editingTask && supervisedTraineeUids.includes(editingTask.assignedToUid))) {
        if (isMounted.current) setError("Permission denied to edit this task.");
        return;
      }

      if (isMounted.current) { setIsProcessing(true); setError(''); setSuccessMessage(''); }

      let traineeDataSource = assignableTrainees;
      if (isCreating && isSupervisor && !canPerformBroadTaskActions) {
        traineeDataSource = assignableTrainees.filter(t => supervisedTraineeUids.includes(t.authUid));
      }

      const assignedTrainee = traineeDataSource.find(t => t.authUid === formData.assignedToUid);

      if (isCreating && !formData.assignedToUid) {
        if (isMounted.current) { setError("You must assign the task to a trainee."); setIsProcessing(false); }
        return;
      }
      if (isCreating && !assignedTrainee && formData.assignedToUid) {
        if (isMounted.current) {
          setError("Selected trainee not found in the current list. The list might be updating or the trainee is not available.");
          setIsProcessing(false);
        }
        return;
      }

      const finalAssignedToUid = formData.assignedToUid;
      const finalAssignedToName = assignedTrainee ? assignedTrainee.name : (formData.assignedToUid ? `UID: ${formData.assignedToUid.substring(0,5)}...` : 'N/A');
      const finalTraineeDocId = assignedTrainee ? assignedTrainee.docId : null;

      if (isCreating && finalAssignedToUid && !finalTraineeDocId) {
        console.error(`Critical Error: TraineeDocId is missing for assigned UID ${finalAssignedToUid}. Name: ${finalAssignedToName}.`);
        if (isMounted.current) {
          setError("Error: The selected trainee's profile is incomplete (missing internal ID). Task cannot be created. Please contact an administrator or select a different trainee.");
          setIsProcessing(false);
          return;
        }
      }

      // 🔔 Vacation warning before save (dueDate day; if no dueDate -> today)
      try {
          const dueStr = formData.dueDate && formData.dueDate.trim() ? formData.dueDate.trim() : fmtYMD(new Date());
          const vacHits = await checkVacationForDayById(finalAssignedToUid, dueStr);

        if (vacHits.length > 0) {
          const list = vacHits.map(v => `${v.date}${v.type ? ` (${v.type})` : ''}`).join(', ');
          const proceed = window.confirm(
            `⚠️ ${finalAssignedToName} is on Vacation on: ${list}.\n\nDo you want to assign the task anyway?`
          );
          if (!proceed) {
            if (isMounted.current) setIsProcessing(false);
            return; // cancelled
          }
        }
    } catch (vacErr) {
       console.error('Vacation check failed:', vacErr);
       if (isMounted.current) {
         setError('Could not verify vacation status. Please try again.');
         setIsProcessing(false);
       }
       return; // block save if we cannot verify
     }

      const taskPayload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        status: formData.status,
        priority: formData.priority,
        assignedToUid: finalAssignedToUid,
        assignedToName: finalAssignedToName,
        traineeDocId: finalTraineeDocId,
        dueDate: formData.dueDate ? Timestamp.fromDate(new Date(formData.dueDate + "T00:00:00")) : null,
        updatedAt: serverTimestamp(),
        taskSystem: 'trainee',
      };

      if (editingTask) {
        const currentTaskData = tasks.find(t => t.id === editingTask.id) || {};
        ['submission', 'acknowledgedAt', 'submittedAt', 'revisionReason', 'revisionRequestedAt', 'completedAt', 'createdByUid', 'createdAt'].forEach(field => {
          if (currentTaskData[field] !== undefined) taskPayload[field] = currentTaskData[field];
        });
        if (!taskPayload.taskSystem || taskPayload.taskSystem !== 'trainee') {
          taskPayload.taskSystem = 'trainee';
        }
        if (formData.status === 'Completed' && !taskPayload.completedAt) {
          taskPayload.completedAt = serverTimestamp();
        } else if (formData.status !== 'Completed' && taskPayload.completedAt) {
          taskPayload.completedAt = null;
        }
      } else {
        taskPayload.createdAt = serverTimestamp();
        taskPayload.createdByUid = currentUser.uid;
        taskPayload.status = formData.status || 'To Do';
      }

      try {
        const taskCollectionRef = collection(db, 'tasks');
        if (editingTask && editingTask.id) {
          const taskRef = doc(taskCollectionRef, editingTask.id);
          await updateDoc(taskRef, taskPayload);
          if (isMounted.current) setSuccessMessage(`Task "${formData.title}" updated successfully.`);
        } else {
          await addDoc(taskCollectionRef, taskPayload);
          if (isMounted.current) setSuccessMessage(`Task "${formData.title}" created successfully.`);
        }
        if (isMounted.current) _hCM();
      } catch (err) {
        console.error("Error saving task:", err);
        if (isMounted.current) setError("Failed to save task. " + err.message);
      } finally {
        if (isMounted.current) setIsProcessing(false);
      }
    };

    const _hDT = async (taskId, taskTitle) => {
      if (!canPerformBroadTaskActions) {
        if (isMounted.current) setError("Permission denied to delete tasks.");
        return;
      }
      if (!window.confirm(`Are you sure you want to delete the task "${taskTitle || 'this task'}"? This action cannot be undone.`)) return;
      if (isMounted.current) { setIsProcessing(true); setError(''); setSuccessMessage(''); }
      try {
        const taskRef = doc(db, 'tasks', taskId);
        await deleteDoc(taskRef);
        if (isMounted.current) setSuccessMessage(`Task "${taskTitle}" deleted successfully.`);
      } catch (err) {
        console.error("Error deleting task:", err);
        if (isMounted.current) setError("Failed to delete task. " + err.message);
      } finally {
        if (isMounted.current) setIsProcessing(false);
      }
    };

    return {
      handleOpenAddTaskModal: _hOATM,
      handleOpenEditTaskModal: _hOETM,
      handleCloseModal: _hCM,
      handleSaveTask: _hST,
      handleDeleteTask: _hDT
    };
  })();

  const handleManagerQuickStatusChange = async (taskId, newStatus) => {
    if (!canPerformBroadTaskActions && !isSupervisor) { if(isMounted.current) setError("Permission denied to change status."); return; }
    const task = tasks.find(t => t.id === taskId);
    if (!task) { if(isMounted.current) setError("Task not found."); return; }

    if (isSupervisor && !canPerformBroadTaskActions) {
      const allowedSupervisorChanges = ['Blocked'];
      if (task.status === 'Blocked' && ['To Do', 'In Progress'].includes(newStatus)) {
        // Allow
      } else if (!allowedSupervisorChanges.includes(newStatus) && newStatus !== task.status) {
        if(isMounted.current) setError(`Supervisors can only change status to 'Blocked' or unblock tasks. For other changes, use workflow actions or contact a manager.`);
        return;
      }
    }

    if (isMounted.current) setIsProcessing(true);
    const taskRef = doc(db, 'tasks', taskId);
    const updatePayload = {
      status: newStatus,
      updatedAt: serverTimestamp(),
      taskSystem: 'trainee'
    };

    if (newStatus === 'Completed') {
      updatePayload.completedAt = serverTimestamp();
      updatePayload.revisionReason = null;
      updatePayload.revisionRequestedAt = null;
      updatePayload.submittedAt = task.submittedAt || serverTimestamp();
      updatePayload.submission = task.submission || { text: "Completed directly by manager/supervisor." };
    } else if (task.completedAt && newStatus !== 'Completed') {
      updatePayload.completedAt = null;
    }

    try {
      await updateDoc(taskRef, updatePayload);
      if (isMounted.current) setSuccessMessage(`Task status updated to "${newStatus}".`);
    } catch (err) {
      console.error("Error updating status:", err);
      if (isMounted.current) setError("Failed to update status. " + err.message);
    } finally {
      if (isMounted.current) setIsProcessing(false);
    }
  };

  const getDisplayedCompletedTasks = () => {
    const allCompletedInColumn = tasksByDisplayColumn['Completed'] || [];
    if (completionFilter === 'all') return allCompletedInColumn;
    const todayForFilter = new Date();
    const startOfDay = new Date(todayForFilter.getFullYear(), todayForFilter.getMonth(), todayForFilter.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(todayForFilter.getFullYear(), todayForFilter.getMonth(), todayForFilter.getDate(), 23, 59, 59, 999);
    if (completionFilter === 'today') {
      return allCompletedInColumn.filter(t => {
        const completedDate = t.completedAt;
        return completedDate && completedDate >= startOfDay && completedDate <= endOfDay;
      });
    }
    if (completionFilter === 'previously') {
      return allCompletedInColumn.filter(t => {
        const completedDate = t.completedAt;
        return completedDate && completedDate < startOfDay;
      });
    }
    return allCompletedInColumn;
  };

  const todayForDateInput = useMemo(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }, []);

  if (loading && tasks.length === 0 && !error && (!isSupervisor || !supervisedTraineeUidsFetched)) { return <div className={styles.loadingIndicator}>Loading tasks...</div>; }
  const fullPageErrorCond = (error && tasks.length === 0 && (error.startsWith("Permission denied") || error.startsWith("Please log in") || error.startsWith("Database index required")));
  if (fullPageErrorCond) { return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>; }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            {isSupervisor && assigneeFilter !== 'all' && assignableTrainees.find(t=>t.authUid === assigneeFilter)
              ? `${assignableTrainees.find(t=>t.authUid === assigneeFilter).name}'s Tasks`
              : (userRole === 'Trainee' ? "My Tasks Dashboard" : "Trainee Tasks Dashboard")
            }
          </h1>
          <p className={styles.subtitle}>
            {userRole === 'Trainee'
              ? 'Track your assigned tasks, submit work and see feedback.'
              : 'Assign, track and review tasks across your trainees.'}
          </p>
        </div>
        {canCreateTasks && (
          <button
            onClick={handleOpenAddTaskModal}
            className={styles.addButton}
            disabled={isProcessingAction || ((userRole === 'Trainee' || isSupervisor || TASK_ACTION_ROLES.includes(userRole)) && assignableTrainees.length === 0 && !loading)}
            title={(userRole === 'Trainee' || isSupervisor || TASK_ACTION_ROLES.includes(userRole)) && assignableTrainees.length === 0 && !loading ? "Trainee list loading..." : "Add New Task"}
          >
            Add New Task
          </button>
        )}
      </div>

      {(userRole === 'Trainee' || (isSupervisor && assigneeFilter !== 'all' && tasks.length > 0)) && (
        <div className={styles.achievementsSection}>
          <div className={styles.achievementsHeader}>
            <h3 className={styles.achievementsTitle}>
              {(userRole === 'Trainee') ? "Your Quick Stats" : "Trainee Quick Stats"}
            </h3>
            <div className={styles.achievementsDateFilters}>
              <label htmlFor="achievements-date-filter" className={styles.dateFilterLabel}>For:</label>
              <select
                id="achievements-date-filter"
                value={achievementsDateFilter}
                onChange={(e) => setAchievementsDateFilter(e.target.value)}
                className={styles.filterSelectSmall}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last28">Last 28 Days</option>
                <option value="custom">Custom Range</option>
                <option value="maximum">All Time</option>
              </select>
              {achievementsDateFilter === 'custom' && (
                <div className={styles.customDateRangePicker}>
                  <input
                    type="date"
                    aria-label="Custom start date for stats"
                    value={customAchievementStartDate}
                    onChange={(e) => setCustomAchievementStartDate(e.target.value)}
                    className={styles.dateInputSmall}
                    max={customAchievementEndDate || todayForDateInput}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    aria-label="Custom end date for stats"
                    value={customAchievementEndDate}
                    onChange={(e) => setCustomAchievementEndDate(e.target.value)}
                    className={styles.dateInputSmall}
                    min={customAchievementStartDate || undefined}
                    max={todayForDateInput}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.achievementsGrid}>
            <div className={styles.achievementCard}>
              <h4>Active Tasks</h4>
              <p className={styles.achievementValue}>{traineeStats.activeTasks}</p>
            </div>
            <div className={styles.achievementCard}>
              <h4>Submitted</h4>
              <p className={styles.achievementValue}>{traineeStats.submittedInRange}</p>
            </div>
            <div className={styles.achievementCard}>
              <h4>Completed</h4>
              <p className={styles.achievementValue}>{traineeStats.completedInRange}</p>
            </div>
            <div className={styles.achievementCard}>
              <h4>Needs Revision</h4>
              <p className={`${styles.achievementValue} ${traineeStats.needsRevisionInRange > 0 ? styles.warningCount : ''}`}>
                {traineeStats.needsRevisionInRange}
              </p>
            </div>
            <div className={styles.achievementCard}>
              <h4>Overdue</h4>
              <p className={`${styles.achievementValue} ${traineeStats.overdueInRange > 0 ? styles.lateCount : ''}`}>
                {traineeStats.overdueInRange}
              </p>
            </div>
            <div className={styles.achievementCard}>
              <h4>Avg. Ack. Time</h4>
              <p className={styles.achievementValue}>{formatAverageDuration(traineeStats.avgAckTime)}</p>
              {traineeStats.ackTasksCount > 0 && <small>({traineeStats.ackTasksCount} tasks)</small>}
            </div>
            <div className={styles.achievementCard}>
              <h4>Avg. Work Cycle <small>(Ack to Submit)</small></h4>
              <p className={styles.achievementValue}>{formatAverageDuration(traineeStats.avgWorkCycleTime)}</p>
              {traineeStats.submittedTasksForCycleCount > 0 && <small>({traineeStats.submittedTasksForCycleCount} tasks)</small>}
            </div>
          </div>
          {achievementsDateFilter === 'custom' && !achievementDateRange.isValid && (!customAchievementStartDate || !customAchievementEndDate) && (
            <p className={styles.customDateInfo}>Please select both start and end dates.</p>
          )}
          {achievementsDateFilter === 'custom' && !achievementDateRange.isValid && customAchievementStartDate && customAchievementEndDate && (
            <p className={styles.customDateError}>End date must be on or after start date.</p>
          )}
        </div>
      )}

      {showAssigneeFilter && (
        <div className={styles.filterContainer}>
          <label htmlFor="assigneeFilter" className={styles.filterLabel}>
            {isSupervisor && !canPerformBroadTaskActions ? "View Trainee:" : "Filter by Assignee:"}
          </label>
          <select id="assigneeFilter" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)} className={styles.filterSelect}>
            <option value="all">
              {isSupervisor && !canPerformBroadTaskActions ? "All My Trainees" : "All Assignees"}
            </option>
            {(isSupervisor && !canPerformBroadTaskActions
              ? assignableTrainees.filter(t => supervisedTraineeUids.includes(t.authUid))
              : assignableTrainees
            ).map(trainee => (
              <option key={trainee.authUid} value={trainee.authUid}>{trainee.name}</option>
            ))}
          </select>
        </div>
      )}

      {error && !showTaskModal && !showSubmissionModal && !showRevisionModal && !fullPageErrorCond && <div className={styles.errorMessage} style={{ marginBottom: '1rem' }}>{error}</div>}
      {successMessage && <div className={styles.successMessage} style={{ marginBottom: '1rem' }}>{successMessage}</div>}

      {(filteredTasksByAssignee.length === 0 && !loading && !error && !fullPageErrorCond) && (
        <p className={styles.noDataMessage}>
          {assigneeFilter !== 'all' ? `No tasks found for the selected assignee.` :
            isSupervisor && supervisedTraineeUidsFetched && supervisedTraineeUids.length === 0 ? "You currently supervise no trainees, or no tasks are assigned to your trainees." :
            (canPerformBroadTaskActions && !isSupervisor && userRole !== 'Trainee') ? "No trainee-related tasks found. Click 'Add New Task' to assign one." :
            (userRole === 'Trainee' ? "You have no tasks assigned. Click 'Add New Task' to create one." : "No tasks to display based on current filters.")
          }
        </p>
      )}

      <div className={styles.tasksContainer}>
        {DISPLAY_COLUMNS_ORDER.map(columnName => {
          let tasksForThisColumn = (columnName === 'Completed') ? getDisplayedCompletedTasks() : (tasksByDisplayColumn[columnName] || []);
          let columnTitle = columnName;

          return (
            <div key={columnName} className={styles.tasksColumn}>
              <div className={styles.columnHeader}>
                <h2 className={styles.columnTitle}>{columnTitle} ({tasksForThisColumn.length})</h2>
                {columnName === 'Completed' && (
                  <div className={styles.completionFilterButtons}>
                    <button onClick={() => setCompletionFilter('all')} className={completionFilter === 'all' ? styles.activeFilterButton : styles.filterButton}>All</button>
                    <button onClick={() => setCompletionFilter('today')} className={completionFilter === 'today' ? styles.activeFilterButton : styles.filterButton}>Today</button>
                    <button onClick={() => setCompletionFilter('previously')} className={completionFilter === 'previously' ? styles.activeFilterButton : styles.filterButton}>Prev.</button>
                  </div>
                )}
              </div>
              <div className={styles.tasksList}>
                {tasksForThisColumn.length > 0 ? tasksForThisColumn.map(task => {
                  const isCurrentUserAssigned = task.assignedToUid === currentUser?.uid;
                  const isSupervisorForThisTask = isSupervisor && supervisedTraineeUids.includes(task.assignedToUid);
                  const canReviewSubmission = (canPerformBroadTaskActions || isSupervisorForThisTask) && task.status === 'Submitted';
                  const displayBadgeStatus = task.status;

                  return (
                    <div key={task.id} className={`${styles.taskCard} ${getStatusClass(displayBadgeStatus, 'border')}`}>
                      <div className={styles.taskHeader}>
                        <h3 className={styles.taskTitle}>{task.title}</h3>
                        <span className={`${styles.statusBadge} ${getStatusClass(displayBadgeStatus, 'badge')}`}>{task.status}</span>
                      </div>
                      {task.description && <p className={styles.taskDescription}>{task.description}</p>}
                      <p className={styles.taskMeta}><strong>Priority:</strong> {task.priority || 'N/A'}</p>
                      {task.dueDate && <p className={styles.taskMeta}><strong>Due:</strong> {formatDate(task.dueDate)}</p>}
                      {(showAssigneeFilter || !isCurrentUserAssigned || canPerformBroadTaskActions || isSupervisor) && <p className={styles.taskMeta}><strong>Assignee:</strong> {task.assignedToName || (task.assignedToUid ? `UID: ${task.assignedToUid.substring(0,5)}...` : 'Unassigned')}</p>}
                      {task.acknowledgedAt && <p className={styles.taskMetaSmall}>Acknowledged: {formatDate(task.acknowledgedAt, { dateStyle: 'short', timeStyle: 'short' })}</p>}
                      {task.submittedAt && task.status !== 'Needs Revision' && <p className={styles.taskMetaSmall}>Submitted: {formatDate(task.submittedAt, { dateStyle: 'short', timeStyle: 'short' })}</p>}
                      {task.completedAt && <p className={styles.taskMetaSmall}>Completed: {formatDate(task.completedAt, { dateStyle: 'short', timeStyle: 'short' })}</p>}

                      {task.status === 'Needs Revision' && task.revisionReason && (
                        <div className={styles.revisionReasonSection}><p><strong>Revision Requested ({task.revisionRequestedAt ? formatDate(task.revisionRequestedAt, { dateStyle: 'short', timeStyle: 'short' }) : 'N/A'}):</strong> {task.revisionReason}</p></div>
                      )}
                      {canReviewSubmission && task.submission && (
                        <div className={styles.submissionReviewSection}>
                          <h4>Submission Details:</h4>
                          {task.submission.text && <p><strong>Notes:</strong> {task.submission.text}</p>}
                          {task.submission.links?.length > 0 && (<div><strong>Links:</strong><ul>{task.submission.links.map((l, i) => <li key={i}><a href={l} target="_blank" rel="noopener noreferrer">{l}</a></li>)}</ul></div>)}
                          {task.submission.files?.length > 0 && (<div><strong>Files (Simulated URLs):</strong><ul>{task.submission.files.map((f, i) => <li key={i}><a href={f.url} target="_blank" rel="noopener noreferrer">{f.name}</a> ({f.size ? `${Math.round(f.size / 1024)}KB` : 'N/A size'})</li>)}</ul></div>)}
                        </div>
                      )}
                      <div className={styles.taskActions}>
                        {isCurrentUserAssigned && userRole === 'Trainee' && !isSupervisor && !canPerformBroadTaskActions && (
                          <>
                            {task.status === 'To Do' && (<button onClick={() => handleAcknowledgeTask(task.id)} className={styles.actionButtonPrimary} disabled={isProcessingAction}>Acknowledge</button>)}
                            {(task.status === 'In Progress' || task.status === 'Needs Revision') && (<button onClick={() => handleOpenSubmissionModal(task)} className={styles.actionButtonPrimary} disabled={isProcessingAction}>{task.status === 'Needs Revision' ? 'Resubmit Work' : 'Submit Work'}</button>)}
                          </>
                        )}
                        {canReviewSubmission && (
                          <>
                            <button onClick={() => handleSupervisorDecision(task.id, 'Completed')} className={`${styles.actionButton} ${styles.approveButton}`} disabled={isProcessingAction}>Approve</button>
                            <button onClick={() => handleSupervisorDecision(task.id, 'Needs Revision')} className={`${styles.actionButton} ${styles.reviseButton}`} disabled={isProcessingAction}>Request Revision</button>
                          </>
                        )}
                        {(canPerformBroadTaskActions || (isSupervisor && isSupervisorForThisTask)) && (
                          <select
                            value={task.status}
                            onChange={(e) => handleManagerQuickStatusChange(task.id, e.target.value)}
                            disabled={isProcessingAction || (!canPerformBroadTaskActions && isSupervisor && (task.status === 'Submitted' || task.status === 'To Do' || task.status === 'In Progress' || task.status === 'Needs Revision' || task.status === 'Completed'))}
                            className={styles.statusSelectManager}
                          >
                            {TASK_STATUSES_INTERNAL.map(s => (
                              <option
                                key={s}
                                value={s}
                                disabled={!canPerformBroadTaskActions && isSupervisor &&
                                  !(s === 'Blocked' ||
                                  (task.status === 'Blocked' && (s === 'To Do' || s === 'In Progress')) ||
                                  s === task.status)
                                }
                              >
                                {s}
                              </option>
                            ))}
                          </select>
                        )}
                        {canPerformBroadTaskActions && (
                          <>
                            <button onClick={() => handleOpenEditTaskModal(task)} className={`${styles.actionButtonSmall} ${styles.editButton}`} disabled={isProcessingAction}>Edit</button>
                            <button onClick={() => handleDeleteTask(task.id, task.title)} className={`${styles.actionButtonSmall} ${styles.deleteButton}`} disabled={isProcessingAction}>Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                }) : (<p className={styles.noTasksInStatus}>No tasks in this column.</p>)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Task Modal */}
      {showTaskModal && canCreateTasks && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>{editingTask ? 'Edit Task' : 'Add New Task'}</h2>
            <form onSubmit={(e) => {
              e.preventDefault(); if (isMounted.current) setError('');
              const formData = {
                title: e.target.title.value, description: e.target.description.value, status: e.target.status.value,
                priority: e.target.priority.value,
                assignedToUid: e.target.assignedToUid.value,
                dueDate: e.target.dueDate.value,
              };
              if (!formData.title.trim() || !formData.priority) { if (isMounted.current) setError("Title & Priority are required."); return; }
              if (!editingTask && !formData.assignedToUid) { if (isMounted.current) setError("Task must be assigned to a trainee."); return; }
              if (!editingTask && !formData.status) formData.status = 'To Do';
              else if (editingTask && !formData.status) formData.status = editingTask.status;
              handleSaveTask(formData);
            }}>
              <div className={styles.formGroup}>
                <label htmlFor="title">Title:</label>
                <input type="text" id="title" name="title" defaultValue={editingTask?.title || ''} required />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="description">Description:</label>
                <textarea id="description" name="description" defaultValue={editingTask?.description || ''} rows="3" />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="status">Status:</label>
                <select id="status" name="status" defaultValue={editingTask?.status || TASK_STATUSES_INTERNAL[0]} required>
                  {TASK_STATUSES_INTERNAL.map(s => <option key={s} value={s} disabled={editingTask && s === 'Submitted' && editingTask.status !== 'Submitted'}>{s}</option>
                  )}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="priority">Priority:</label>
                <select id="priority" name="priority" defaultValue={editingTask?.priority || TASK_PRIORITIES[1]} required>
                  {TASK_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="assignedToUid">Assign To:</label>
                <select
                  id="assignedToUid" name="assignedToUid"
                  defaultValue={editingTask?.assignedToUid || ''}
                  required={!editingTask}
                  disabled={ (!!editingTask && !canPerformBroadTaskActions && !(isSupervisor && editingTask && supervisedTraineeUids.includes(editingTask.assignedToUid))) }
                >
                  <option value="">Select Trainee</option>
                  {
                    ( (userRole === 'Trainee' || canPerformBroadTaskActions)
                      ? assignableTrainees
                      : (isSupervisor
                          ? assignableTrainees.filter(t => supervisedTraineeUids.includes(t.authUid))
                          : []
                        )
                    ).map(trainee => (
                      <option key={trainee.authUid} value={trainee.authUid}>{trainee.name}</option>
                    ))
                  }
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="dueDate">Due Date (Optional):</label>
                <input type="date" id="dueDate" name="dueDate" defaultValue={editingTask?.dueDate ? formatDate(editingTask.dueDate, 'yyyy-MM-dd') : ''} />
              </div>
              {error && showTaskModal && <p className={styles.errorMessageModal}>{error}</p>}
              <div className={styles.modalActions}>
                <button type="button" onClick={handleCloseAddEditModal} disabled={isProcessingAction} className={styles.cancelButton}>Cancel</button>
                <button type="submit" disabled={isProcessingAction} className={styles.saveButton}>
                  {isProcessing ? 'Saving...' : (editingTask ? 'Save Changes' : 'Create Task')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSubmissionModal && taskForSubmission && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContentLg}>
            <h2>Submit Work for: {taskForSubmission.title}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmitWork(); }}>
              <div className={styles.formGroup}>
                <label htmlFor="submissionText">Notes/Text Description:</label>
                <textarea id="submissionText" value={submissionText} onChange={(e) => setSubmissionText(e.target.value)} rows="5" placeholder="Add your notes or text-based submission here..." className={styles.submissionTextarea} disabled={isProcessingAction || isUploadingFile}/>
              </div>
              <p className={styles.orSeparator}>OR</p>
              <div className={styles.formGroup}>
                <label>Links (up to 10):</label>
                {submissionLinks.map((link, index) => (
                  <div key={index} className={styles.linkInputGroup}>
                    <input type="url" placeholder="https://example.com" value={link} onChange={(e) => updateLinkField(index, e.target.value)} disabled={isProcessingAction || isUploadingFile}/>
                    {submissionLinks.length > 1 && <button type="button" onClick={() => removeLinkField(index)} className={styles.removeButton} disabled={isProcessingAction || isUploadingFile}>&times;</button>}
                  </div>
                ))}
                {submissionLinks.length < 10 && <button type="button" onClick={addLinkField} className={styles.addButtonSmall} disabled={isProcessingAction || isUploadingFile}>+ Add Link</button>}
              </div>
              <p className={styles.orSeparator}>OR</p>
              <div className={styles.formGroup}>
                <label htmlFor="submissionFiles">Files (up to 10, max 5MB each recommended):</label>
                <input type="file" id="submissionFiles" multiple onChange={handleFileSelect} ref={fileInputRef} accept="image/*,application/pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.rar" disabled={isProcessingAction || isUploadingFile}/>
                <ul className={styles.fileList}>
                  {submissionFiles.map(file => (
                    <li key={file.name}>{file.name} ({Math.round(file.size / 1024)} KB)
                      {fileUploadProgress[file.name] < 100 && fileUploadProgress[file.name] !== undefined ? ` (Uploading: ${fileUploadProgress[file.name]}%)` : (fileUploadProgress[file.name] === 100 ? ' (Uploaded)' : '')}
                      <button type="button" onClick={() => removeSubmissionFile(file.name)} className={styles.removeButtonSmall} disabled={isProcessingAction || isUploadingFile}>Remove</button>
                    </li>
                  ))}
                </ul>
                <small>Selected files: {submissionFiles.length}/10. Max 5MB per file.</small>
              </div>
              {submitError && showSubmissionModal && <p className={styles.errorMessageModal}>{submitError}</p>}
              <div className={styles.modalActions}>
                <button type="button" onClick={handleCloseSubmissionModal} disabled={isSubmitting || isUploadingFile} className={styles.cancelButton}>Cancel</button>
                <button type="submit" disabled={isSubmitting || isUploadingFile || (!submissionText.trim() && submissionLinks.every(link => !link.trim()) && submissionFiles.length === 0)} className={styles.saveButton}>
                  {isSubmitting ? 'Submitting...' : (isUploadingFile ? 'Uploading...' : 'Submit Work')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showRevisionModal && taskForRevisionRequest && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <h2>Request Revision for: {taskForRevisionRequest.title}</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleSubmitRevisionRequest(); }}>
              <div className={styles.formGroup}>
                <label htmlFor="revisionReasonText">Reason for Revision (Required):</label>
                <textarea id="revisionReasonText" value={revisionReasonText} onChange={(e) => setRevisionReasonText(e.target.value)} rows="4" required className={styles.fullWidthTextarea} disabled={isProcessing}/>
              </div>
              {error && showRevisionModal && <p className={styles.errorMessageModal}>{error}</p>}
              <div className={styles.modalActions}>
                <button type="button" onClick={handleCloseRevisionModal} disabled={isProcessing} className={styles.cancelButton}>Cancel</button>
                <button type="submit" disabled={isProcessing || !revisionReasonText.trim()} className={styles.saveButton}>{isProcessing ? 'Submitting...' : 'Submit Request'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TraineeTasksDashboardPage;
